/**
 * FollowButton — small pill toggle used on places + category pages.
 * Silent no-op for signed-out users (redirects to /signin).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, HeartOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/follows`;

export default function FollowButton({ targetType, targetSlug, size = "sm" }) {
  const { user } = useAuth();
  const [following, setFollowing] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    fetch(`${API}/status?target_type=${targetType}&target_slug=${encodeURIComponent(targetSlug)}`,
          { credentials: "include" })
      .then(r => r.ok ? r.json() : { following: false })
      .then((d) => setFollowing(!!d.following))
      .catch(() => setFollowing(false));
  }, [user, targetType, targetSlug]);

  if (!user) {
    return (
      <Link
        to={`/signin?next=${encodeURIComponent(window.location.pathname)}`}
        data-testid={`follow-signin-${targetType}-${targetSlug}`}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-slate-200 bg-white text-slate-700 hover:border-blue-400 hover:text-blue-700"
      >
        <Heart className="w-3 h-3" /> Follow
      </Link>
    );
  }

  const toggle = async () => {
    setBusy(true);
    try {
      if (following) {
        const r = await fetch(`${API}?target_type=${targetType}&target_slug=${encodeURIComponent(targetSlug)}`,
                              { method: "DELETE", credentials: "include" });
        if (!r.ok) throw new Error(await r.text());
        setFollowing(false);
      } else {
        const r = await fetch(API, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ target_type: targetType, target_slug: targetSlug }),
        });
        if (!r.ok) throw new Error(await r.text());
        setFollowing(true);
        toast.success(`Following · we'll surface this on your home feed.`);
      }
    } catch (e) {
      toast.error(String(e.message || e));
    } finally {
      setBusy(false);
    }
  };

  const active = "bg-blue-600 text-white border-blue-600";
  const inactive = "bg-white text-slate-700 border-slate-200 hover:border-blue-400";
  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      data-testid={`follow-btn-${targetType}-${targetSlug}`}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${following ? active : inactive}`}
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" />
        : following ? <HeartOff className="w-3 h-3" /> : <Heart className="w-3 h-3" />}
      {following ? "Following" : "Follow"}
    </button>
  );
}
